import { useAtomValue } from 'jotai'
import { useContext } from 'react'

import { LinkingPageContext } from '@/features/dashboard/lib/linking-page-context'
import globalStore from '@/lib/configs/store-config'
import { computeOrderFormTarget } from '@/lib/resolve-order-form-target'
import { linkingColorsAtomFamily } from '@/state/linking'
import {
  lastFocusedOrderCardAtom,
  type OrderFormSelection,
  orderFormSelectionAtomFamily,
  registeredOrderContainersAtom,
} from '@/state/order'

/**
 * Returns the cardId of whichever Order Form surface (OrderContainer widget or popup)
 * is the current target — i.e. the SAME resolution `resolveOrderFormTarget` (the write
 * side, used by `useOrderFormDispatch`/`useOrderFormPopup`) computes. Falls back to
 * `myCardId` only when nothing is registered at all (popup-only, no on-page widget).
 *
 * Rows only move within a link group: a widget resolves to the Order Form in its own
 * group, and a mounted Order Form resolves to itself (see computeOrderFormTarget).
 *
 * Both source atoms live on the global store; pin them explicitly via { store } so this
 * still resolves correctly when called from inside a scoped JotaiProvider (e.g. the
 * order-form popup's isolated chat store) — otherwise it silently reads that empty
 * scoped store and falls back to `myCardId` even when a real target is registered.
 */
export function useOrderFormTargetId(myCardId: string): string {
  const lastFocused = useAtomValue(lastFocusedOrderCardAtom, { store: globalStore })
  const registered = useAtomValue(registeredOrderContainersAtom, { store: globalStore })
  const pageId = useContext(LinkingPageContext)
  const linkingColors = useAtomValue(linkingColorsAtomFamily(pageId), { store: globalStore })
  return computeOrderFormTarget(lastFocused, registered, myCardId, linkingColors, pageId)
}

/**
 * Returns the selection state at `useOrderFormTargetId`'s resolved target.
 *
 * Must use the exact same resolution as `resolveOrderFormTarget` (the write side used
 * by `useOrderFormDispatch`/`useOrderFormPopup`) — previously this had its own inline
 * logic that skipped the "fall back to the first registered container" step — so once
 * `lastFocusedOrderCardAtom` was cleared (e.g. by clicking anywhere outside an order
 * container, like the watchlist itself), the read here fell back to `myCardId` while
 * writes still correctly resolved to the registered widget's cardId. That mismatch made
 * callers like the watchlist's Bid/Ask toggle/side-flip logic always see an empty
 * selection and re-add instead of removing/flipping, and made the embedded widget look
 * unresponsive.
 */
export function useOrderFormSelectionTarget(myCardId: string): OrderFormSelection {
  const targetId = useOrderFormTargetId(myCardId)
  return useAtomValue(orderFormSelectionAtomFamily(targetId), { store: globalStore })
}
